/**
 * `?dev` console hooks for authoring the crow tour on an SDK scene — legacy's
 * `window.__crowTour`, without the pose readers the dev panel already has.
 * Session-only: a `play` patch mutates the live config; copy results into the
 * scene file by hand.
 */
import type { DatumEngine } from '@datum-sdk/engine';
import type { CrowTourConfig } from '../../sceneConfig';
import type { CrowOpeningTour } from './crowOpeningTour';

/** The SDK's own cap on walking the colliders stacked under one pixel. */
const MAX_PICK_STEPS = 16;

const round = (value: number): number => Math.round(value * 1e4) / 1e4;

export const exposeCrowTourDevHooks = (
  engine: DatumEngine,
  settings: CrowTourConfig,
  tour: CrowOpeningTour,
): (() => void) => {
  const hooks = {
    /** The tour again, with `patch` merged into its config — no reload, no preloader. */
    play: (patch?: Partial<CrowTourConfig>): CrowTourConfig => {
      Object.assign(settings, patch);
      tour.replay();
      return settings;
    },
    /** One exact frame of the chase, held; a tap leaves it, as a skip. */
    scrub: (progress: number) => tour.scrub(progress),
    /**
     * Splats cannot be raycast, but the colliders trace the masonry the splat
     * shows: the world point under a viewport pixel on collider `colliderId`.
     * That is how the archway waypoints were measured.
     */
    pick: (x: number, y: number, colliderId = 'kaaba-cube') => {
      let near: number | undefined;
      for (let step = 0; step < MAX_PICK_STEPS; step++) {
        const hit = engine.pickCollider(x, y, { near });
        if (!hit) return null;
        if (hit.colliderId === colliderId) {
          const [hitX, hitY, hitZ] = hit.point;
          return { x: round(hitX), y: round(hitY), z: round(hitZ) };
        }
        near = hit.distance + 1e-4;
      }
      return null;
    },
  };
  const host = window as { __crowTour?: typeof hooks };
  host.__crowTour = hooks;
  return () => {
    if (host.__crowTour === hooks) delete host.__crowTour;
  };
};
